import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import ConnectionsHealth from "../components/ConnectionsHealth";
import { RentreeActionsCard } from "../components/rentree/RentreeActionsCard";
import { BeltWallCard } from "../components/BeltWallCard";
import { BirthdayCard } from "../components/BirthdayCard";
import { BoussoleCard } from "../components/BoussoleCard";
import { InspirationCard } from "../components/InspirationCard";
import { PageHeader } from "../components/ui/kaizen";
import {
  Sun, RefreshCw, CalendarClock, Megaphone, CreditCard, Users, School,
  AlertTriangle, CheckCircle2, Clock, ArrowRight, Mail, Bell, Send, MessageSquare, Zap,
} from "lucide-react";

const fmtDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "long" });
  } catch { return d; }
};
const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

function Section({ title, icon: Icon, accent, to, count, children, testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border ${accent} bg-zinc-900/60 backdrop-blur p-5 flex flex-col`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <Icon className="w-5 h-5 opacity-90" />
          <h2 className="text-base font-semibold tracking-tight">{title}</h2>
          {typeof count === "number" && (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white/10">{count}</span>
          )}
        </div>
        {to && (
          <Link to={to} data-testid={`${testid}-open`}
            className="text-xs text-zinc-400 hover:text-white flex items-center gap-1 transition-colors">
            Ouvrir <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
      <div className="flex-1 space-y-2 text-sm">{children}</div>
    </div>
  );
}

const Empty = ({ text }) => <p className="text-zinc-500 text-sm italic">{text}</p>;

const Stat = ({ label, value, tone = "text-zinc-200" }) => (
  <div className="flex items-baseline justify-between border-b border-white/5 py-1.5 last:border-0">
    <span className="text-zinc-400">{label}</span>
    <span className={`font-bold tabular-nums ${tone}`}>{value}</span>
  </div>
);

export default function MonMatinModule() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [digest, setDigest] = useState(null);
  const [showDigest, setShowDigest] = useState(false);
  const [testing, setTesting] = useState(false);
  const [videoCov, setVideoCov] = useState(null);

  const loadVideoCov = useCallback(async () => {
    try {
      const [r, g] = await Promise.all([
        api.get("/academy/admin/curriculum/video-stats"),
        api.get("/academy/admin/curriculum/video-goal").catch(() => ({ data: null })),
      ]);
      const ds = r.data.disciplines || [];
      const total = ds.reduce((s, d) => s + d.total, 0);
      const withV = ds.reduce((s, d) => s + d.with_video, 0);
      setVideoCov({ total, withV, missing: total - withV, disciplines: ds, goal: g.data });
    } catch { /* noop */ }
  }, []);

  const setVideoGoal = async () => {
    const cur = videoCov?.goal?.weekly_goal || 50;
    const v = window.prompt("Objectif de vidéos à ajouter par semaine :", String(cur));
    if (!v) return;
    const n = parseInt(v, 10);
    if (!n || n < 1) { toast.error("Nombre invalide"); return; }
    try {
      await api.post("/academy/admin/curriculum/video-goal", { weekly_goal: n });
      toast.success(`Objectif hebdo : ${n} vidéos`);
      loadVideoCov();
    } catch { toast.error("Erreur"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/mon-matin/summary");
      setData(r.data);
    } catch {
      // Tableau réservé à l'admin : on n'alarme pas les rôles équipe.
      if (user?.is_admin || user?.role === "admin") toast.error("Erreur de chargement du tableau « Mon matin »");
    }
    setLoading(false);
  }, [user]);

  const loadDigest = useCallback(async () => {
    try { const r = await api.get("/mon-matin/digest/settings"); setDigest(r.data); } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); loadDigest(); loadVideoCov(); }, [load, loadDigest, loadVideoCov]);

  const saveDigest = async (patch) => {
    const next = { ...digest, ...patch };
    setDigest(next);
    try { await api.put("/mon-matin/digest/settings", patch); toast.success("Réglage enregistré"); }
    catch { toast.error("Échec de l'enregistrement"); loadDigest(); }
  };

  const sendTestDigest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/mon-matin/digest/run?dry_run=false");
      if (r.data?.ok) toast.success(`Résumé envoyé ✅ ${r.data.email ? "courriel" : ""}${r.data.email && r.data.sms ? " + " : ""}${r.data.sms ? "SMS" : ""}`);
      else toast.error("Aucun envoi — vérifie courriel/SMS dans les réglages");
    } catch { toast.error("Échec de l'envoi du test"); }
    setTesting(false);
  };

  const sendTestEvening = async () => {
    setTesting(true);
    try {
      const r = await api.post("/mon-matin/evening-report/run?dry_run=false");
      if (r.data?.ok) toast.success(`Bilan du soir envoyé 🌙 ${r.data.email ? "courriel" : ""}${r.data.email && r.data.sms ? " + " : ""}${r.data.sms ? "SMS" : ""}`);
      else toast.error("Aucun envoi — vérifie courriel/SMS dans les réglages");
    } catch { toast.error("Échec de l'envoi du test"); }
    setTesting(false);
  };

  const sendPaymentLink = async (id) => {
    setSending(id);
    try {
      const r = await api.post(`/member-payments/payments/${id}/send-payment-link`);
      if (r.data?.ok) toast.success("Lien de paiement envoyé (courriel + SMS)");
      else toast.error("Envoi partiel — vérifie le contact du membre");
    } catch { toast.error("Échec de l'envoi du lien"); }
    setSending(null);
  };

  const [reminding, setReminding] = useState(null);
  const sendReminder = async (id) => {
    setReminding(id);
    try {
      const r = await api.post(`/member-payments/payments/${id}/send-reminder`);
      if (r.data?.ok) toast.success("Relance envoyée (courriel + SMS)");
      else toast.error("Relance partielle — vérifie le contact du membre");
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de la relance"); }
    setReminding(null);
  };

  if (loading && !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-zinc-400">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Préparation de ta matinée…
      </div>
    );
  }

  const a = data?.agenda || {};
  const ap = data?.autopilot || {};
  const pay = data?.payments || {};
  const leads = data?.leads || {};
  const ecoles = data?.ecoles || {};
  const inbox = data?.inbox || {};

  return (
    <div data-testid="mon-matin-module" className="text-zinc-100 max-w-7xl p-6 md:p-8 lg:p-10">
      <PageHeader
        title="Mon matin"
        icon={Sun}
        subtitle={<span className="capitalize">{fmtDate(data?.as_of)}</span>}
        actions={
          <>
            <Link data-testid="mon-matin-courriels" to="/app/courriels"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-200 text-sm transition-colors">
              <Mail className="w-4 h-4" /> Boîte courriel
            </Link>
            <button data-testid="mon-matin-digest-toggle" onClick={() => setShowDigest((v) => !v)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm transition-colors">
              <Bell className="w-4 h-4 text-amber-300" /> Résumé 7h
            </button>
            <button data-testid="mon-matin-refresh" onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
            </button>
          </>
        }
      />

      {/* Santé des connexions (Gmail / Facebook / Instagram / TikTok / Stripe) */}
      <ConnectionsHealth />

      {/* Réglages du résumé matinal 7h */}
      {showDigest && digest && (
        <div data-testid="mon-matin-digest-panel"
          className="mb-8 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-300" /> Résumé matinal automatique (7h)
            </h2>
            <button data-testid="digest-enabled-toggle" onClick={() => saveDigest({ enabled: !digest.enabled })}
              className={`text-xs font-bold px-3 py-1 rounded-full transition-colors ${digest.enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-700 text-zinc-400"}`}>
              {digest.enabled ? "● Activé" : "○ Désactivé"}
            </button>
          </div>
          <p className="text-zinc-400 text-sm mb-4">
            Chaque matin à 7h, reçois un résumé (RDV, cartes refusées, versements en retard, leads, relances Écoles)
            par courriel et/ou SMS. Et chaque soir (~20h), un <span className="text-indigo-300">bilan « tout a tourné »</span>
            {" "}(pubs publiées, échecs, prospects relancés) — pour démarrer et finir ta journée sans ouvrir l'app.
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            <button data-testid="digest-email-toggle" onClick={() => saveDigest({ send_email: !digest.send_email })}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition-colors ${digest.send_email ? "bg-sky-500/15 border-sky-500/40 text-sky-200" : "bg-white/5 border-white/10 text-zinc-400"}`}>
              <Mail className="w-4 h-4" /> Courriel {digest.send_email ? "ON" : "OFF"}
            </button>
            <button data-testid="digest-sms-toggle" onClick={() => saveDigest({ send_sms: !digest.send_sms })}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition-colors ${digest.send_sms ? "bg-violet-500/15 border-violet-500/40 text-violet-200" : "bg-white/5 border-white/10 text-zinc-400"}`}>
              <MessageSquare className="w-4 h-4" /> SMS {digest.send_sms ? "ON" : "OFF"}
            </button>
            <button data-testid="digest-test-btn" onClick={sendTestDigest} disabled={testing}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-amber-500/90 hover:bg-amber-500 text-black font-medium transition-colors disabled:opacity-50">
              <Send className="w-4 h-4" /> {testing ? "Envoi…" : "Envoyer un test maintenant"}
            </button>
            <button data-testid="evening-test-btn" onClick={sendTestEvening} disabled={testing}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-indigo-500/20 border border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/30 font-medium transition-colors disabled:opacity-50">
              🌙 {testing ? "Envoi…" : "Tester le bilan du soir"}
            </button>
          </div>
        </div>
      )}

      {(inbox.reengaged_now ?? 0) > 0 && (
        <Link to="/app/conversion-globale" data-testid="mm-reengaged-alert"
          className="mb-6 flex items-center justify-between gap-4 rounded-2xl border border-red-500/40 bg-red-500/10 px-5 py-4 hover:bg-red-500/15 transition-colors group">
          <span className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20 text-xl">🔥</span>
            <span>
              <span className="block font-bold text-red-200">{inbox.reengaged_now} prospect(s) re-engagé(s) à rappeler MAINTENANT</span>
              <span className="block text-sm text-red-300/80">Ils ont re-cliqué ton lien après ta relance — ce sont tes leads les plus chauds.</span>
            </span>
          </span>
          <span className="flex items-center gap-1 text-sm text-red-200 font-medium shrink-0 group-hover:translate-x-0.5 transition-transform">
            Voir <ArrowRight className="w-4 h-4" />
          </span>
        </Link>
      )}

      {videoCov && videoCov.total > 0 && (
        <Link to="/app/videos-groupe" data-testid="video-coverage-gauge"
          className="block rounded-2xl border border-violet-500/30 bg-violet-500/5 hover:bg-violet-500/10 transition-colors p-5 mb-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-violet-300 text-sm font-semibold">📺 Couverture vidéo du curriculum</span>
            </div>
            <span className="text-xs text-zinc-400">
              {videoCov.withV} / {videoCov.total} techniques · <span className="text-amber-400 font-semibold">{videoCov.missing} sans vidéo</span>
            </span>
          </div>
          <div className="h-3 rounded-full bg-zinc-800 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-violet-500 to-emerald-400 transition-all"
              style={{ width: `${Math.round((videoCov.withV / videoCov.total) * 100)}%` }} />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-2xl font-black text-violet-200">{Math.round((videoCov.withV / videoCov.total) * 100)}%</span>
            <span className="text-xs text-violet-300 hover:underline">Compléter les vidéos manquantes →</span>
          </div>
          {videoCov.goal && (
            <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between" data-testid="video-weekly-goal"
              onClick={(e) => { e.preventDefault(); setVideoGoal(); }}>
              <span className="text-xs text-zinc-300 flex items-center gap-2">
                🎯 Objectif hebdo : <span className="font-bold text-emerald-300">{videoCov.goal.added_this_week}</span> / {videoCov.goal.weekly_goal} vidéos cette semaine
                {videoCov.goal.added_this_week >= videoCov.goal.weekly_goal && (
                  <span className="ml-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[11px] font-bold animate-pulse" data-testid="goal-reached-badge">🎉 Objectif atteint !</span>
                )}
              </span>
              <span className="text-[11px] text-zinc-500 hover:text-zinc-300">Modifier l'objectif</span>
            </div>
          )}
        </Link>
      )}

      <RentreeActionsCard />
      <BeltWallCard />
      <BirthdayCard />
      <BoussoleCard />
      <InspirationCard />
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
        {/* Agenda */}
        <Section title="Agenda du jour" icon={CalendarClock} accent="border-sky-500/30"
          to="/app/morning-brief" count={a.meetings_today_count} testid="mm-agenda">
          {(a.meetings_today || []).length === 0
            ? <Empty text="Aucun rendez-vous détecté aujourd'hui." />
            : a.meetings_today.map((m, i) => (
              <div key={i} className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 text-sky-400 shrink-0" />
                <div>
                  <p className="font-medium">{m.title || "Rendez-vous"}</p>
                  <p className="text-zinc-400 text-xs">{fmtTime(m.datetime_iso)} {m.with ? `· ${m.with}` : ""}</p>
                </div>
              </div>
            ))}
          {a.b2b_replies_count > 0 && (
            <p className="text-amber-300/90 text-xs flex items-center gap-1.5 pt-1">
              <Mail className="w-3.5 h-3.5" /> {a.b2b_replies_count} courriel(s) B2B à répondre
            </p>
          )}
        </Section>

        {/* Pilote auto */}
        <Section title="Pilote réseaux sociaux" icon={Megaphone} accent="border-fuchsia-500/30"
          to="/app/pilote-reseaux" testid="mm-autopilot">
          <Stat label="Publiés aujourd'hui" value={ap.published_today ?? 0} tone="text-emerald-400" />
          <Stat label="Échecs aujourd'hui" value={ap.failed_today ?? 0}
            tone={(ap.failed_today ?? 0) > 0 ? "text-red-400" : "text-zinc-400"} />
          <Stat label="Programmés à venir" value={ap.scheduled_count ?? 0} />
          <Stat label="À approuver" value={ap.pending_approval ?? 0}
            tone={(ap.pending_approval ?? 0) > 0 ? "text-amber-300" : "text-zinc-400"} />
          {(ap.scheduled_next || []).slice(0, 3).map((p, i) => (
            <div key={i} className="text-xs text-zinc-400 flex items-center gap-1.5 pt-1">
              <Clock className="w-3 h-3" /> {fmtTime(p.scheduled_at)} · {p.angle_label || p.discipline || "post"}
            </div>
          ))}
        </Section>

        {/* Paiements */}
        <Section title="Paiements" icon={CreditCard} accent="border-emerald-500/30"
          to="/app/contrats-paiements"
          count={(pay.overdue_count ?? 0) + (pay.declined_count ?? 0)} testid="mm-paiements">
          {(pay.declined_count ?? 0) > 0 && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-2 mb-1">
              <p className="text-red-300 text-xs font-semibold flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> {pay.declined_count} carte(s) refusée(s) — à relancer
              </p>
            </div>
          )}
          {(pay.cards_expiring_count ?? 0) > 0 && (
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2 mb-1" data-testid="mm-cards-expiring">
              <p className="text-amber-300 text-xs font-semibold flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> {pay.cards_expiring_count} carte(s) expire(nt) d'ici 30 jours
              </p>
              {pay.cards_expiring.slice(0, 4).map((c) => <p key={c.email} className="text-[11px] text-zinc-300 pl-5">{c.name || c.email} · {String(c.brand || "").toUpperCase()} •••• {c.last4} · {c.expired ? "EXPIRÉE" : `${c.days_left} j`} ({c.expires_on})</p>)}
            </div>
          )}
          <Stat label="En retard" value={`${pay.overdue_count ?? 0} · ${(pay.overdue_amount ?? 0).toFixed(2)} $`}
            tone={(pay.overdue_count ?? 0) > 0 ? "text-red-400" : "text-zinc-400"} />
          <Stat label="Dus cette semaine" value={`${pay.due_soon_count ?? 0} · ${(pay.due_soon_amount ?? 0).toFixed(2)} $`} />
          {(pay.overdue || []).slice(0, 4).map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 pt-1">
              <span className="text-xs text-zinc-300 truncate">{p.member_name} · {p.amount} $</span>
              <div className="flex items-center gap-1.5 shrink-0">
                <button data-testid={`mm-pay-remind-${p.id}`} disabled={reminding === p.id}
                  onClick={() => sendReminder(p.id)}
                  className="text-[11px] px-2 py-0.5 rounded-md bg-amber-500/90 hover:bg-amber-500 text-zinc-950 font-semibold disabled:opacity-50">
                  {reminding === p.id ? "…" : "🔔 Relancer"}
                </button>
                <button data-testid={`mm-pay-link-${p.id}`} disabled={sending === p.id}
                  onClick={() => sendPaymentLink(p.id)}
                  className="text-[11px] px-2 py-0.5 rounded-md bg-violet-600/80 hover:bg-violet-600 text-white disabled:opacity-50">
                  {sending === p.id ? "…" : "🔗 Lien"}
                </button>
              </div>
            </div>
          ))}
        </Section>

        {/* Leads */}
        <Section title="Nouveaux leads (essai)" icon={Users} accent="border-blue-500/30"
          to="/app/crm-leads" count={leads.new_count} testid="mm-leads">
          {(leads.new || []).length === 0
            ? <Empty text="Aucun nouveau lead à rappeler." />
            : leads.new.slice(0, 6).map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-2 border-b border-white/5 py-1.5 last:border-0">
                <div className="min-w-0">
                  <p className="font-medium truncate">{l.name}</p>
                  <p className="text-zinc-400 text-xs truncate">{l.phone || l.email} {l.program ? `· ${l.program}` : ""}</p>
                </div>
                {l.preferred_date && (
                  <span className="text-[11px] text-blue-300 shrink-0">{l.preferred_date}</span>
                )}
              </div>
            ))}
        </Section>

        {/* Programme Écoles */}
        <Section title="Programme Écoles" icon={School} accent="border-teal-500/30"
          to="/app/programme-ecoles"
          count={(ecoles.school_followups_count ?? 0) + (ecoles.offer_followups_count ?? 0)} testid="mm-ecoles">
          <Stat label="Écoles à relancer" value={ecoles.school_followups_count ?? 0}
            tone={(ecoles.school_followups_count ?? 0) > 0 ? "text-amber-300" : "text-zinc-400"} />
          <Stat label="Offres en attente de relance" value={ecoles.offer_followups_count ?? 0}
            tone={(ecoles.offer_followups_count ?? 0) > 0 ? "text-amber-300" : "text-zinc-400"} />
          {(ecoles.school_followups || []).slice(0, 4).map((s, i) => (
            <div key={i} className="text-xs text-zinc-300 flex items-center gap-1.5 pt-1">
              <School className="w-3 h-3 text-teal-400" /> {s.name} <span className="text-zinc-500">· {s.date}</span>
            </div>
          ))}
        </Section>

        {/* Inbox sociale — relances auto des prospects chauds */}
        <Section title="Inbox — relances auto" icon={Zap} accent="border-amber-500/30"
          to="/app/conversion-globale"
          count={inbox.auto_relanced_24h ?? 0} testid="mm-inbox-relance">
          {(inbox.auto_relanced_24h ?? 0) === 0 && (inbox.hot_to_relance ?? 0) === 0 ? (
            <Empty text="Aucune relance auto cette nuit. Tes prospects chauds sont à jour." />
          ) : (
            <>
              <Stat label="Prospects relancés automatiquement (24h)" value={inbox.auto_relanced_24h ?? 0}
                tone={(inbox.auto_relanced_24h ?? 0) > 0 ? "text-emerald-300" : "text-zinc-400"} />
              <Stat label="Ont re-cliqué après la relance 🔥" value={inbox.reclicked_after_relance ?? 0}
                tone={(inbox.reclicked_after_relance ?? 0) > 0 ? "text-amber-300" : "text-zinc-400"} />
              <Stat label="Prospects chauds en attente de relance" value={inbox.hot_to_relance ?? 0}
                tone={(inbox.hot_to_relance ?? 0) > 0 ? "text-amber-300" : "text-zinc-400"} />
              {(inbox.auto_relanced_24h ?? 0) > 0 && (
                <p className="text-xs text-emerald-300/80 pt-1.5 flex items-center gap-1.5">
                  <Zap className="w-3 h-3" /> Ton Inbox a converti pendant ton sommeil.
                </p>
              )}
            </>
          )}
        </Section>

        {/* Carte « Tout est calme » */}
        <Section title="Résumé" icon={CheckCircle2} accent="border-zinc-700" testid="mm-resume">
          <p className="text-zinc-400 text-sm leading-relaxed">
            Ton tableau « Mon matin » réunit tout le flux quotidien Kaizen.
            Traite d'abord les <span className="text-red-300">cartes refusées</span> et
            les <span className="text-blue-300">nouveaux leads</span>, puis vérifie que
            le <span className="text-fuchsia-300">pilote réseaux</span> a bien publié.
          </p>
        </Section>
      </div>
    </div>
  );
}
